import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Mail, MessageCircle, PenSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldWrapper } from '@/components/ui/field-wrapper';
import { senderProfilesApi } from '@/features/sender-profiles/api';
import { composeApi } from './api';

export function ComposePage() {
  const [channel, setChannel] = useState<'Email' | 'WhatsApp'>('Email');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [profileId, setProfileId] = useState('default');

  const profilesQuery = useQuery({
    queryKey: ['sender-profiles'],
    queryFn: () => senderProfilesApi.list(),
  });
  const profiles = (profilesQuery.data ?? []).filter((p) => p.Channel === channel);

  const sendMutation = useMutation({
    mutationFn: () =>
      composeApi.send({
        channel,
        to: to
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean),
        subject: subject || undefined,
        body,
        senderProfileId: profileId === 'default' ? undefined : profileId,
      }),
    onSuccess: (res) => {
      toast.success(`${res.queued.length} message(s) queued — track them in Delivery Log.`);
      setTo('');
      setSubject('');
      setBody('');
    },
    onError: (e: Error) => toast.error(e.message || 'Send failed.'),
  });

  const recipients = to
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <PenSquare size={20} className="text-[#008ED3]" />
        <h1 className="text-xl font-bold text-gray-900">Compose Message</h1>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/30 p-4">
          <CardTitle className="text-sm font-semibold text-gray-700">Message Configuration</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FieldWrapper label="Channel" required>
              <Select
                value={channel}
                onValueChange={(val) => {
                  setChannel(val as 'Email' | 'WhatsApp');
                  setProfileId('default');
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Email">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-blue-500" />
                      Email
                    </div>
                  </SelectItem>
                  <SelectItem value="WhatsApp">
                    <div className="flex items-center gap-2">
                      <MessageCircle size={14} className="text-green-500" />
                      WhatsApp
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </FieldWrapper>

            <FieldWrapper label="Sender profile">
              <Select
                value={profileId}
                onValueChange={setProfileId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Channel default" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Channel default</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.SenderProfileID} value={p.SenderProfileID}>
                      {p.ProfileName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldWrapper>
          </div>

          <FieldWrapper
            label={`Recipients (${channel === 'Email' ? 'emails' : 'phone numbers'})`}
            required
            hint="Separate multiple entries with commas or newlines."
          >
            <Textarea
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder={channel === 'Email' ? 'a@x.com, b@y.com' : '+9198…, +9197…'}
              className="min-h-[80px]"
            />
          </FieldWrapper>

          {channel === 'Email' && (
            <FieldWrapper label="Subject" required>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Monthly Report for October"
              />
            </FieldWrapper>
          )}

          <FieldWrapper
            label={channel === 'Email' ? 'Message content (HTML allowed)' : 'Message text'}
            required
          >
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[200px]"
              placeholder="Type your message here..."
            />
          </FieldWrapper>

          <div className="flex justify-end pt-2 border-t mt-6">
            <Button
              size="lg"
              onClick={() => sendMutation.mutate()}
              disabled={
                recipients.length === 0 ||
                !body ||
                (channel === 'Email' && !subject) ||
                sendMutation.isPending
              }
              className="px-8 shadow-md"
            >
              <Send size={16} className="mr-2" />
              {sendMutation.isPending
                ? 'Queuing…'
                : recipients.length > 0
                ? `Send to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`
                : 'Send message'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
